import { useRuntimeInfo } from '@account/account-portal'
import {
  startExternalAuthFlow,
  useExternalAuthData,
  ExternalAuthError,
  ExternalAuthPageProps,
  saveExternalAuthData,
} from '@account/account-portal/external-auth'
import { resetExternalAuthState, setExternalAuthError } from '@account/account-portal/lib'
import { GlButton } from '@adl/component-button'
import { GlList, GlListItem, GL_LIST_AS } from '@adl/component-list'
import { GlModal } from '@adl/component-modal'
import { evaluateConfig } from '@crystal/core'
import { useTranslations } from '@crystal/translator'
import { FC } from 'react'

import { config } from '../config'

import { isKnownError, UNKNOWN_ERROR } from './errors'

export const ExternalAuthErrorModal: FC<ExternalAuthPageProps> = ({ className }) => {
  const t = useTranslations()
  const { callCenterNumber } = evaluateConfig(config)
  const externalAuthData = useExternalAuthData()
  const { authContext, error } = externalAuthData
  const { isAuthProvider } = useRuntimeInfo()

  function cancelRetry() {
    if (authContext?.appRedirect?.path) {
      externalAuthData.authContext = {
        ...externalAuthData.authContext,
        isCancelled: true,
      }
      saveExternalAuthData(externalAuthData)
      return window.location.assign(authContext.appRedirect.path)
    }
    setExternalAuthError(new ExternalAuthError('Retry cancelled').serialize())
  }

  function retryExternalAuthentication() {
    if (!authContext?.flow) {
      return
    }

    startExternalAuthFlow(authContext.flow)
    resetExternalAuthState()
  }

  function getErrorCode({ serverErrorType, serverErrorCode }: ExternalAuthError) {
    if (serverErrorType != 'server_error' || !serverErrorCode) {
      return UNKNOWN_ERROR
    }
    return isKnownError(serverErrorCode) ? serverErrorCode : UNKNOWN_ERROR
  }

  const RetryButton = () => {
    if (error?.visible === false) {
      return null
    }

    return (
      <GlButton
        className="gl-vspace-bpall-medium"
        fullWidth
        onClick={retryExternalAuthentication}
        data-auto-id="glass-external-auth-error-modal-retry-button">
        {t('account.external.authentication.error.retry.button.label')}
      </GlButton>
    )
  }

  const KmcErrorContent = () => (
    <>
      <p className="gl-body--s gl-vspace">{t('account.external.authentication.error.reason.title')}</p>
      <GlList as={GL_LIST_AS.ul}>
        <GlListItem>{t('account.external.authentication.error.reason.list.item')}</GlListItem>
      </GlList>
      <RetryButton />
      <p className="gl-body--s gl-vspace">{t('account.external.authentication.error.callCenter.instruction')}</p>
      <p className="gl-body--s gl-vspace">
        {t.element('account.external.authentication.error.callCenter.numberWithLabel', callCenterNumber)}
      </p>
      <GlButton
        href={`tel:${callCenterNumber}`}
        fullWidth
        data-auto-id="glass-external-auth-error-modal-call-center-button"
        variant="secondary">
        {t('account.external.authentication.error.callCenter.button.label')}
      </GlButton>
    </>
  )

  const SocialLoginErrorContent = () => {
    if (!error) {
      return null
    }

    return (
      <>
        <p className="gl-vspace-bpall-small">{t(`account.external.authentication.errorCode.${getErrorCode(error)}`)}</p>
        <RetryButton />
      </>
    )
  }

  if (!error) {
    return null
  }

  return (
    <GlModal
      id="mf-externalauth"
      closeButtonLabel=""
      disablePortal
      open={true}
      className={className}
      title={t('account.external.authentication.error.title')}
      size="regular"
      contentClassName="gl-external-auth-error-modal"
      showCloseButton={!isAuthProvider}
      closeOnOverlayClick={!isAuthProvider}
      onClose={cancelRetry}>
      {authContext?.flow?.method === 'kmc' && <KmcErrorContent />}
      {authContext?.flow?.method === 'social-login' && <SocialLoginErrorContent />}
    </GlModal>
  )
}
